import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { uniqBy } from 'lodash';

import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Link from 'components/common/Link';
import Chip from 'components/common/Chip';
import Tooltip from 'components/common/Tooltip';
import Button from 'components/common/Button';
import IconDocument from 'components/icons/IconDocument';
import IconArrowLeft from 'components/icons/IconArrowLeft';

import formatPrice from 'utils/formatPrice';

import pxToRem from 'styles/pxToRem';
import hexToRgb from 'styles/hexToRgb';

const styles = (theme) => ({
  avatar: {
    display: 'inline-block',
    verticalAlign: 'middle',
    width: 32,
    height: 32,
    marginRight: 15,
  },
  iconMoreVert: {
    width: 24,
    height: 24,
  },
  buttonCell: {
    textAlign: 'right',
    padding: '0 10px 0 0',
    '&:last-child': {
      padding: '0 10px 0 0',
    },
  },
  tableCellRoles: {
    maxWidth: 200,
  },
  cellStatus: {
    textTransform: 'capitalize',
    fontWeight: 'bold',
  },
  cellStatePaid: {
    color: theme.palette.add['500'],
  },
  cellStatePartial: {
    color: theme.palette.notification[500],
  },
  cellStateRecouped: {
    color: theme.palette.states.collection,
  },
  cellStateDenied: {
    color: theme.palette.states.rejected,
  },
  header: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '16px 40px 16px 50px',
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 4,
      paddingRight: 4,
    },
  },
  typographyLabel: {
    marginRight: 4,
  },
  typographyValue: {
    marginRight: 16,
    fontSize: pxToRem(12),
    fontWeight: 500,
  },
  typographyValuePositive: {
    color: theme.palette.add['500'],
  },
  typographyValueNegative: {
    color: theme.palette.validation['500'],
  },
  iconEra: {
    position: 'relative',
    display: 'inline-block',
    verticalAlign: 'middle',
    marginLeft: 4,
    marginBottom: -3,
    marginTop: -5,
  },
  iconEraDocument: {
    width: 18,
    height: 18,
    color: theme.palette.grey[600],
  },
  iconEraText: {
    display: 'block',
    position: 'absolute',
    paddingLeft: 2,
    paddingRight: 2,
    bottom: 0,
    right: 0,
    fontSize: pxToRem(7),
    fontWeight: 700,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary[500],
    textShadow: 'none',
  },
  linkEra: {
    whiteSpace: 'nowrap',
  },
  iconArrowLeft: {
    width: 16,
    height: 16,
    marginRight: 8,
  },
  iconArrowRight: {
    width: 14,
    height: 14,
    transform: 'rotate(180deg)',
    marginLeft: 8,
  },
  buttonBack: {
    marginTop: -8,
    marginBottom: -8,
    marginRight: 16,
  },
  buttonWorkflow: {
    marginTop: -8,
    marginBottom: -8,
  },
  tooltip: {
    '& + &': {
      marginLeft: 4,
    },
  },
  chip: {
    maxWidth: 130,
  },
  chipCO: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.primary[500])}, 0.4)`,
  },
  chipOA: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.notification[500])}, 0.4)`,
  },
  chipPR: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.states.paid)}, 0.5)`,
  },
  chipCR: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.states.collection)}, 0.4)`,
  },
  chipPI: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.states.intake)}, 0.4)`,
  },
  tableCellActions: {
    width: 130,
  },
});

const EraChartClaimsTable = ({ eraData, checkNumber, onERADownload, onBackButtonClick, classes }) => {
  const totalClaimsCount = (eraData.Paid
      ? eraData.Paid['Claims Count'] : 0)
    + (eraData['Partial Regular']
      ? eraData['Partial Regular']['Claims Count'] : 0)
    + (eraData['Partial Recoverable']
      ? eraData['Partial Recoverable']['Claims Count'] : 0)
    + (eraData.Recouped
        ? eraData.Recouped['Claims Count'] : 0)
    + (eraData.Denied
      ? eraData.Denied['Claims Count'] : 0);
  const totalPatientsCount = (eraData.Paid
        ? eraData.Paid['Patients Count'] : 0)
    + (eraData['Partial Regular']
      ? eraData['Partial Regular']['Patients Count'] : 0)
    + (eraData['Partial Recoverable']
      ? eraData['Partial Recoverable']['Patients Count'] : 0)
    + (eraData.Recouped
      ? eraData.Recouped['Patients Count'] : 0)
    + (eraData.Denied
      ? eraData.Denied['Patients Count'] : 0);
  const totalBilledReimbs = (eraData.Paid
        ? eraData.Paid['Billed Reimbs'] : 0)
    + (eraData['Partial Regular']
      ? eraData['Partial Regular']['Billed Reimbs'] : 0)
    + (eraData['Partial Recoverable']
      ? eraData['Partial Recoverable']['Billed Reimbs'] : 0)
    + (eraData.Recouped
      ? eraData.Recouped['Billed Reimbs'] : 0)
    + (eraData.Denied
      ? eraData.Denied['Billed Reimbs'] : 0);
  const totalPaidReimbs = (eraData.Paid
        ? eraData.Paid['Paid Reimbs'] : 0)
    + (eraData['Partial Regular']
      ? eraData['Partial Regular']['Paid Reimbs'] : 0)
    + (eraData['Partial Recoverable']
      ? eraData['Partial Recoverable']['Paid Reimbs'] : 0)
    + (eraData.Recouped
      ? eraData.Recouped['Paid Reimbs'] : 0)
    + (eraData.Denied
      ? eraData.Denied['Paid Reimbs'] : 0);
  return (
    <div>
      <div className={classes.header}>
        <Button
          className={classes.buttonBack}
          variant="raised"
          onClick={onBackButtonClick}
        > 
          <IconArrowLeft className={classes.iconArrowLeft} />
          Back
        </Button>
        <Typography variant="caption" className={classes.typographyLabel}>ERA</Typography>
        <Typography className={classes.typographyValue}>
          <Link
            className={classes.linkEra}
            onClick={() => {
              onERADownload(eraData.ERA['ERA ID']);
            }}
            target="_blank"
          >
            {checkNumber}
            <div className={classes.iconEra}>
              <IconDocument className={classes.iconEraDocument} />
              <span className={classes.iconEraText}>ERA</span>
            </div>
          </Link>
        </Typography>
        <Typography variant="caption" className={classes.typographyLabel}>Date</Typography>
        <Typography className={classes.typographyValue}>{eraData.Date}</Typography>
        <Typography variant="caption" className={classes.typographyLabel}>Claims</Typography>
        <Typography className={classes.typographyValue}>{totalClaimsCount}</Typography>
        <Typography variant="caption" className={classes.typographyLabel}>Patients</Typography>
        <Typography className={classes.typographyValue}>{totalPatientsCount}</Typography>
        <Typography variant="caption" className={classes.typographyLabel}>Billed</Typography>
        <Typography className={classes.typographyValue}>{formatPrice(totalBilledReimbs)}</Typography>
        <Typography variant="caption" className={classes.typographyLabel}>Paid</Typography>
        <Typography
          className={classNames(classes.typographyValue, {
            [classes.typographyValuePositive]: totalPaidReimbs > 0,
            [classes.typographyValueNegative]: totalPaidReimbs <= 0,
          })}
        >
          {formatPrice(totalPaidReimbs)}
        </Typography>
        <Button
          className={classes.buttonWorkflow}
          variant="raised"
          onClick={() => {
            window.open(`/claims/claim-md?check-number=${checkNumber}`, '_blank');
          }}
        > 
          Claims
          <IconArrowLeft className={classes.iconArrowRight} />
        </Button>
      </div>
      <Table className={classes.table}>
        <TableHead
          className={classes.tableHead}
        >
          <TableRow>
            <TableCell>Order #</TableCell>
            <TableCell>Patient</TableCell>
            <TableCell>State</TableCell>
            {/* <TableCell>Date of Service</TableCell> */}
            <TableCell numeric>Billed</TableCell>
            <TableCell numeric>Amount Paid</TableCell>     
            <TableCell numeric>CARC</TableCell>
            <TableCell className={classes.tableCellActions} numeric>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {eraData.ERA['ERA Claim'].map((claimItem) => {
            const uniqueAdjustmentCodes = uniqBy(claimItem['Claim Adjustment'].map(adjustmentItem => ({
              label: adjustmentItem.Group
                ? `${adjustmentItem.Group.Code} - ${adjustmentItem.Code.Code}`
                  : adjustmentItem.Code.Code,
              description: adjustmentItem.Code.Description,
              action: adjustmentItem.Action,
              groupCode: adjustmentItem.Group.Code,
            })), 'label');
            let state = claimItem.State ? claimItem.State : '';
            if (state === 'Partial Regular' || state === 'Partial Recoverable') {
              state = 'Partially Paid';
            }
            return (
              <TableRow
                key={claimItem.ERA}
                hover
                onClick={() => {
                  window.open(`/charts/${claimItem.Patient['Record Number']}/summary?order-id=${claimItem['Order ID']}`, '_blank');
                }}
              >
                <TableCell>
                  {claimItem['Order ID']}
                </TableCell>
                <TableCell>
                  {`${claimItem.Patient['First Name']} ${claimItem.Patient['Last Name']}`}
                </TableCell>
                <TableCell
                  className={classNames(classes.cellStatus, {
                    [classes.cellStatePaid]: claimItem.State && claimItem.State === 'Paid',
                    [classes.cellStatePartial]: claimItem.State
                      && (claimItem.State === 'Partial Regular' || claimItem.State === 'Partial Recoverable'),
                    [classes.cellStateRecouped]: claimItem.State && claimItem.State === 'Recouped',
                    [classes.cellStateDenied]: claimItem.State && claimItem.State === 'Denied',
                  })}
                >
                  {state}
                </TableCell>
                {/* <TableCell>
                  {eraItem.Date}
                </TableCell> */}
                <TableCell numeric>
                  {formatPrice(claimItem['Submitted Amount'])}
                </TableCell>
                <TableCell numeric>
                  {formatPrice(claimItem.Paid)}
                </TableCell>
                <TableCell numeric>
                  {uniqueAdjustmentCodes.map((adjustmentCode) => (
                    <Tooltip
                      key={adjustmentCode.label}
                      className={classes.tooltip}
                      content={(
                        <Typography>
                          {adjustmentCode.description}
                        </Typography>
                      )}
                      placement="top"
                      color="white"
                    >
                      <Chip
                        className={classes.chip}
                        label={adjustmentCode.label}
                        size="sm"
                        square
                      />
                    </Tooltip>
                  ))}
                </TableCell>
                <TableCell className={classes.tableCellActions} numeric>
                  {uniqueAdjustmentCodes.map((adjustmentCode) => adjustmentCode.action ? (
                    <Tooltip
                      className={classes.tooltip}
                      key={adjustmentCode.label}
                      content={(
                        <Typography>
                          {adjustmentCode.action}
                        </Typography>
                      )}
                      placement="top"
                      color="white"
                    >
                      <Chip
                        className={classNames(classes.chip, {
                          [classes.chipCO]: adjustmentCode.groupCode === 'CO',
                          [classes.chipOA]: adjustmentCode.groupCode === 'OA',
                          [classes.chipPR]: adjustmentCode.groupCode === 'PR',
                          [classes.chipCR]: adjustmentCode.groupCode === 'CR',
                          [classes.chipPI]: adjustmentCode.groupCode === 'PI',
                        })}
                        label={adjustmentCode.action}
                        size="sm"
                        square
                      />
                    </Tooltip>
                  ) : '')}
                </TableCell>
              </TableRow>
            )})}
          </TableBody>
        </Table>
      </div>
    );
  };

EraChartClaimsTable.propTypes = {
  classes: PropTypes.shape({
    iconMoreVert: PropTypes.string.isRequired,
  }).isRequired,
};

export default withStyles(styles)(EraChartClaimsTable);



// WEBPACK FOOTER //
// ./src/components/home/EraChart/EraChartClaimsTable.js